/**
 * End-of-shift HOTO message: the car field's "DCar HOTO: / Z1: 1-8 / ..." text, built from
 * the roster's last 15-minute slot (utils/hotoMessage.ts). Tick the bikes counters that are
 * open, adjust the text if needed, then copy it or send it on WhatsApp — the same copy and
 * WhatsApp buttons (and contact picker + QR step) the officer cards use.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/api/client';
import { useNotification } from '@/contexts/NotificationContext';
import { ContactPicker } from '@/components/ContactPicker';
import { WaIcon, WhatsAppQrStep } from '@/components/WhatsAppShared';
import type { RecipientPill } from '@/components/WhatsAppShared';
import type { ContactList, OfficerMetadata } from '@/types/api';
import { buildHotoMessage, NO_BIKES, type HotoBikes } from '@/utils/hotoMessage';
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

interface HotoMessageModalProps {
  operationMode?: string;
  officerSchedules: Record<string, number[]>;
  officerMetadata: Record<string, OfficerMetadata>;
  /** officer key -> the note typed in the officer graph's HOTO note column. */
  notes: Record<string, string>;
  onClose: () => void;
}

/** Same Singapore-number tidy-up the officer card's WhatsApp flow does. */
function normaliseSgPhone(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 8 && /^[689]/.test(digits)) digits = '65' + digits;
  return digits;
}

export function HotoMessageModal({
  operationMode, officerSchedules, officerMetadata, notes, onClose,
}: HotoMessageModalProps) {
  const { notifyError } = useNotification();
  const isArrival = operationMode === 'arrival';
  const [bikes, setBikes] = useState<HotoBikes>(NO_BIKES);
  // null = follow the generated text; a string = the user edited it by hand.
  const [edited, setEdited] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [picking, setPicking] = useState(false);
  const [contacts, setContacts] = useState<ContactList | null>(null);
  const [recipient, setRecipient] = useState<{ phone: string; label: string; pill?: RecipientPill } | null>(null);
  const [waMessage, setWaMessage] = useState('');

  const generated = useMemo(
    () => buildHotoMessage({ operationMode, officerSchedules, officerMetadata, notes, bikes }),
    [operationMode, officerSchedules, officerMetadata, notes, bikes],
  );
  const text = edited ?? generated;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (key: keyof HotoBikes) => {
    setBikes((b) => ({ ...b, [key]: !b[key] }));
    setEdited(null); // a changed checkbox regenerates the text
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => notifyError('Could not copy to the clipboard'),
    );
  };

  const handleOpenWhatsApp = async () => {
    setPicking(true);
    if (contacts === null) {
      try {
        setContacts(await apiClient.getContacts());
      } catch {
        setContacts({ teams: [], contacts: [], updated_at: null, updated_by: null, filename: null });
      }
    }
  };

  const handlePick = (phone: string, label: string, pill?: RecipientPill) => {
    const digits = normaliseSgPhone(phone);
    if (!digits) { notifyError('No phone number available for this contact'); return; }
    setWaMessage(text);
    setRecipient({ phone: digits, label, pill });
  };

  const checkboxes: Array<{ key: keyof HotoBikes; label: string }> = isArrival
    ? [{ key: 'bikes1', label: 'BIKES 1' }, { key: 'bikes2', label: 'BIKES 2' }]
    : [{ key: 'dbikes', label: 'DBIKES' }];

  const shell = (children: React.ReactNode) => createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', overflow: 'hidden', zoom: UI_SCALE, maxHeight: popupMaxH(86) }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );

  if (recipient) {
    return shell(
      <WhatsAppQrStep
        phone={recipient.phone}
        message={waMessage}
        onMessageChange={setWaMessage}
        recipientLabel={recipient.label}
        recipientPill={recipient.pill}
        onBack={() => setRecipient(null)}
        onClose={onClose}
      />,
    );
  }

  if (picking) {
    return shell(
      <>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <WaIcon size={20} />
            <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Send HOTO to WhatsApp</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#000', padding: 0 }}>✕</button>
        </div>
        <ContactPicker data={contacts} onPick={(r) => handlePick(r.phone, r.label, r.pill)} />
        <div style={{ padding: '10px 18px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          <button onClick={() => setPicking(false)}
            style={{ padding: '7px 16px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
            Back
          </button>
        </div>
      </>,
    );
  }

  return shell(
    <>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{isArrival ? 'ACar' : 'DCar'} HOTO message</span>
        <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#000', padding: 0 }}>✕</button>
      </div>
      <div style={{ padding: '12px 18px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 13, color: '#1e293b' }}>
          {checkboxes.map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={bikes[key]} onChange={() => toggle(key)}
                style={{ width: 16, height: 16, accentColor: '#667eea' }} />
              {label}
            </label>
          ))}
        </div>
        <textarea
          value={text}
          onChange={(e) => setEdited(e.target.value)}
          rows={Math.min(18, text.split('\n').length + 1)}
          spellCheck={false}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7,
            fontSize: 13, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
        />
        {edited !== null && edited !== generated && (
          <button onClick={() => setEdited(null)}
            style={{ alignSelf: 'flex-start', padding: '4px 10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 12, cursor: 'pointer' }}>
            Reset to generated text
          </button>
        )}
      </div>
      <div style={{ padding: '10px 18px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
        <button title="Send via WhatsApp" onClick={handleOpenWhatsApp}
          style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 5, cursor: 'pointer', padding: '4px 9px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
          <WaIcon size={16} />
        </button>
        <button title={copied ? 'Copied!' : 'Copy message'} onClick={handleCopy}
          style={{ background: copied ? '#d1fae5' : '#f8fafc', border: '1px solid #000', color: copied ? '#065f46' : '#475569', borderRadius: 5, cursor: 'pointer', padding: '5px 11px', fontSize: 13, fontWeight: 600 }}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
    </>,
  );
}
